<template>
  <div class="embedded-signup-form">
    <div v-if="isLoading" class="embedded-signup-form__loading">
      <div class="spinner"></div>
      <p>Cargando formulario...</p>
    </div>
    
    <!-- ThunderLabs embedded form container -->
    <div id="thunderlabs-form-footer-container" ref="formContainerRef"></div>
    
    <div v-if="hasError" class="embedded-signup-form__error">
      <p>Error al cargar el formulario. Por favor, intenta más tarde.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';

const isLoading = ref(true);
const hasError = ref(false);
const formContainerRef = ref(null);
let scriptTag = null;

// Production form embed URL
const FORM_EMBED_URL = 'https://mapp.thunderlabs.cl/f/918caa69-dab4-475d-88f5-de3105025fdc/embed.js';

const cleanupEmbed = () => {
  if (scriptTag && scriptTag.parentNode) {
    scriptTag.parentNode.removeChild(scriptTag);
    scriptTag = null;
  }
  if (formContainerRef.value) {
    formContainerRef.value.innerHTML = '';
  }
};

onMounted(() => {
  nextTick(() => {
    const container = formContainerRef.value;
    if (!container) {
      console.error('Footer form container not found');
      hasError.value = true;
      isLoading.value = false;
      return;
    }

    // Load ThunderLabs embed script
    const script = document.createElement('script');
    script.src = FORM_EMBED_URL;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-target', '#thunderlabs-form-footer-container');

    script.onload = () => {
      isLoading.value = false;
    };

    script.onerror = () => {
      console.error('Failed to load ThunderLabs form');
      hasError.value = true;
      isLoading.value = false;
    };

    scriptTag = script;
    container.appendChild(script);
  });
});

onUnmounted(() => {
  cleanupEmbed();
});
</script>

<style lang="scss" scoped>
@import '@/design-system/variables';

.embedded-signup-form {
  width: 100%;
  
  // ThunderLabs form styles - dark theme integration
  :deep(#thunderlabs-form-footer-container) {
    /* Ensure form inherits dark theme */
    background: transparent;
    color: inherit;
    
    /* Input styling override for dark theme */
    input[type="email"],
    input[type="text"] {
      background: rgba(255, 255, 255, 0.1) !important;
      border: 2px solid transparent !important;
      color: #dfe6e9 !important;
      padding: 14px 16px !important;
      border-radius: 6px !important;
      font-size: 15px !important;
      transition: all 0.3s ease !important;
      
      &::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
      
      &:focus {
        outline: none !important;
        border-color: $tl-primary !important;
        background: rgba(255, 255, 255, 0.15) !important;
      }
    }
    
    /* Button styling override for dark theme */
    button {
      background: $tl-primary !important;
      color: white !important;
      padding: 14px 32px !important;
      border-radius: 6px !important;
      font-size: 15px !important;
      font-weight: 600 !important;
      border: none !important;
      white-space: nowrap !important;
      box-shadow: 0 2px 8px rgba($tl-primary, 0.3) !important;
      transition: all 0.3s ease !important;
      cursor: pointer !important;
      
      &:hover:not(:disabled) {
        background: darken($tl-primary, 10%) !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 4px 12px rgba($tl-primary, 0.4) !important;
      }
      
      &:disabled {
        background: #636e72 !important;
        opacity: 0.5 !important;
        cursor: not-allowed !important;
      }
    }
    
    /* Label and text styling */
    label,
    .form-label {
      color: rgba(255, 255, 255, 0.8) !important;
      font-size: 14px !important;
      font-weight: 400 !important;
      margin-bottom: 4px !important;
    }
    
    /* Error messages */
    .error-message,
    .form-error {
      color: #ff7675 !important;
      font-size: 13px !important;
      margin-top: 4px !important;
    }
    
    /* Success messages */
    .success-message,
    .form-success {
      color: #00b894 !important;
      font-size: 14px !important;
      margin-top: 4px !important;
    }
  }
}

.embedded-signup-form__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $tl-space-3;
  padding: $tl-space-8;
  color: rgba(255, 255, 255, 0.7);
  font-size: $tl-font-size-sm;
  
  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-top-color: $tl-primary;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}

.embedded-signup-form__error {
  padding: $tl-space-6;
  background: rgba(255, 118, 117, 0.1);
  border: 1px solid #ff7675;
  border-radius: $tl-radius-lg;
  color: #ff7675;
  font-size: $tl-font-size-sm;
  text-align: center;
  
  p {
    margin: 0;
  }
}
</style>
